import { useLocation, useParams } from 'react-router';
import { useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import {
  usePSD2OfflineAccountsQuery,
  usePSD2OfflineInitPaymentMutation,
  usePSD2ProxyAccountsQuery,
  usePSD2ProxyInitPaymentMutation,
} from 'hooks/webapi';
import useRedirect from 'hooks/webapi/useRedirect';
import {
  OFFLINE_PSD2_BANK_ACCOUNT_SELECT,
  OFFLINE_PSD2_ERROR,
  PSD2_ERROR,
} from 'Router/routes';
import useNavigateSearch from 'hooks/useNavigateSearch';
import useError from 'hooks/useError';
import { PSD2ClientAccount } from 'hooks/webapi/psd/usePsd2Offline';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import SelectEvent from 'hooks/context/EventCollector/events/SelectEvent';

export default function usePSD2AccountSelection() {
  const navigateSearch = useNavigateSearch();
  const redirect = useRedirect();
  const params = useParams();
  const location = useLocation();
  const { showError } = useError();
  const isOffline = location.pathname.includes(
    OFFLINE_PSD2_BANK_ACCOUNT_SELECT,
  );

  const { track } = useEventCollector();
  const [selectedIBAN, setSelectedIBAN] = useState('');
  const [accountList, setAccountList] = useState<PSD2ClientAccount[]>([]);

  const { data: bankAccountsData, isLoading: bankAccountsIsLoading } =
    usePSD2ProxyAccountsQuery(!isOffline);
  const {
    data: offlineBankAccountsData,
    isLoading: offlineBankAccountsIsLoading,
  } = usePSD2OfflineAccountsQuery(params.token, isOffline);

  const {
    mutateAsync: initializePSD2IdentificationPayment,
    isPending: initPaymentIsPending,
  } = usePSD2ProxyInitPaymentMutation();
  const {
    mutateAsync: offlineInitializePSD2IdentificationPayment,
    isPending: offlineIsPending,
  } = usePSD2OfflineInitPaymentMutation();

  const handleSelectAccount = (iban: string) => {
    setSelectedIBAN(iban);
  };

  const toggleAccounts = () => {
    if (accountList.length > 1) {
      const newList = [];
      newList.push(accountList[0]);
      setAccountList(newList);
      setSelectedIBAN(accountList[0].iban);
    } else if (isOffline) {
      if (offlineBankAccountsData?.bankAccounts) {
        setAccountList(offlineBankAccountsData?.bankAccounts);
      }
    } else if (bankAccountsData?.bankAccounts) {
      setAccountList(bankAccountsData?.bankAccounts);
    }
  };

  // Only one bank account available
  useEffect(() => {
    if (
      bankAccountsData?.bankAccounts &&
      bankAccountsData.bankAccounts.length === 1
    ) {
      setSelectedIBAN(bankAccountsData.bankAccounts[0].iban);
      return;
    }

    if (
      offlineBankAccountsData?.bankAccounts &&
      offlineBankAccountsData.bankAccounts.length === 1
    ) {
      setSelectedIBAN(offlineBankAccountsData.bankAccounts[0].iban);
    }
  }, [bankAccountsData?.bankAccounts, offlineBankAccountsData?.bankAccounts]);

  useEffect(() => {
    if (isOffline && !params.token) {
      const sessionToken = sessionStorage.getItem('offline-psd2-token');
      if (!sessionToken) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }
      navigateSearch(`${OFFLINE_PSD2_BANK_ACCOUNT_SELECT}/${sessionToken}`, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (isOffline) {
      if (offlineBankAccountsData?.bankAccounts) {
        setAccountList(offlineBankAccountsData?.bankAccounts);
      }
    } else if (bankAccountsData?.bankAccounts) {
      setAccountList(bankAccountsData?.bankAccounts);
    }
  }, [isOffline, offlineBankAccountsData, bankAccountsData]);

  const handlePSD2IdentificationPayment = async () => {
    if (!selectedIBAN) {
      return;
    }

    let response;
    if (isOffline) {
      if (!params.token) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }

      response = await offlineInitializePSD2IdentificationPayment({
        token: params.token,
        clientIBAN: selectedIBAN,
      });
    } else {
      response = await initializePSD2IdentificationPayment({
        clientIban: selectedIBAN,
      });
    }

    // NOTE: Temporary solution - sessionStorage is getting invalidated, that's why we are storing data in cookie. Would be good to sort out.
    Cookie.set('token', sessionStorage.getItem('token') || '', {
      expires: 10 * 60000,
    });

    Cookie.set(
      'crossLoginToken',
      sessionStorage.getItem('crossLoginToken') || '',
      {
        expires: 10 * 60000,
      },
    );

    redirect(response.url);
  };

  useEffect(() => {
    if (!isOffline && !bankAccountsData) {
      return;
    }

    if (isOffline && !offlineBankAccountsData) {
      return;
    }

    if (
      !isOffline &&
      bankAccountsData?.bankAccounts &&
      bankAccountsData.bankAccounts.length === 0
    ) {
      navigateSearch(PSD2_ERROR);
      return;
    }

    if (
      isOffline &&
      offlineBankAccountsData?.bankAccounts &&
      offlineBankAccountsData.bankAccounts.length === 0
    ) {
      navigateSearch(OFFLINE_PSD2_ERROR);
    }
  }, [
    bankAccountsData?.bankAccounts,
    offlineBankAccountsData?.bankAccounts,
    isOffline,
  ]);

  // Track selected account
  useEffect(() => {
    if (selectedIBAN === '') {
      return;
    }

    track(
      new SelectEvent({
        description: 'PSD2BankAccountSelect',
        value: selectedIBAN,
      }),
    );
  }, [selectedIBAN]);

  return {
    accountList,
    selectedIBAN,
    toggleAccounts,
    handleSelectAccount,
    isLoading: isOffline ? offlineBankAccountsIsLoading : bankAccountsIsLoading,
    handlePSD2IdentificationPayment,
    isPending: initPaymentIsPending || offlineIsPending,
  };
}
